// This file mirrors the Ruby HouseholdMembership::Role T::Enum
// from monorails/app/lib/household_membership/role.rb.
// Keep the values in sync with the GraphQL HouseholdMembershipRole enum.

export const HouseholdMembershipRoleValues = [
  'owner',
  'co_owner',
  'trustee',
  'co_trustee',
  'beneficiary',
  'authorized_viewer',
] as const;

export type HouseholdMembershipRole = (typeof HouseholdMembershipRoleValues)[number];

export function isOwnerLike(role: HouseholdMembershipRole): boolean {
  return role === 'owner' || role === 'co_owner';
}

export function isTrusteeLike(role: HouseholdMembershipRole): boolean {
  return role === 'trustee' || role === 'co_trustee';
}

// Trustees and co-trustees get the same write-access tier as owners and
// co-owners for Phase 1; beneficiaries and authorized viewers cannot
// initiate destructive actions.
export function canWriteHouseholdData(role: HouseholdMembershipRole): boolean {
  return isOwnerLike(role) || isTrusteeLike(role);
}

export function isValidHouseholdMembershipRole(value: string): value is HouseholdMembershipRole {
  return HouseholdMembershipRoleValues.some((validValue) => validValue === value);
}

// Human-readable labels + inline descriptions for the advisor-side
// Add Household Member dialog. Grouped by account-holder status so
// advisors can see at a glance which roles confer account signing authority.
export type HouseholdMembershipRoleOption = {
  readonly value: HouseholdMembershipRole;
  readonly label: string;
  readonly description: string;
};

export const HouseholdMembershipAccountHolderRoleOptions: readonly HouseholdMembershipRoleOption[] = [
  { value: 'owner', label: 'Owner', description: 'Primary client on the household. Full access.' },
  { value: 'co_owner', label: 'Co-owner', description: 'Joint client (spouse or partner). Full access.' },
  { value: 'trustee', label: 'Trustee', description: 'Legal trustee of a trust-owned household. Full access.' },
  { value: 'co_trustee', label: 'Co-trustee', description: 'Joint trustee. Full access.' },
];

export const HouseholdMembershipNonAccountHolderRoleOptions: readonly HouseholdMembershipRoleOption[] = [
  { value: 'beneficiary', label: 'Beneficiary', description: 'Named beneficiary. Read-only.' },
  {
    value: 'authorized_viewer',
    label: 'Authorized viewer',
    description: 'Additional read-only viewer (e.g., adult child, CPA).',
  },
];

export const HouseholdMembershipRoleOptions: readonly HouseholdMembershipRoleOption[] = [
  ...HouseholdMembershipAccountHolderRoleOptions,
  ...HouseholdMembershipNonAccountHolderRoleOptions,
];

export function labelForHouseholdMembershipRole(role: HouseholdMembershipRole): string {
  return HouseholdMembershipRoleOptions.find((o) => o.value === role)?.label ?? role;
}

const ACCOUNT_HOLDER_ROLE_VALUES = new Set<HouseholdMembershipRole>(
  HouseholdMembershipAccountHolderRoleOptions.map((opt) => opt.value),
);

export function defaultsForRole(role: HouseholdMembershipRole): {
  readonly isAccountHolder: boolean;
  readonly canSignClientAgreement: boolean;
} {
  const isAccountHolder = ACCOUNT_HOLDER_ROLE_VALUES.has(role);
  return { isAccountHolder, canSignClientAgreement: isAccountHolder };
}
